import { isExist } from "cxa-utils";
import api from "../api/flexapi";
import { updateIntl } from "./../i18n/ReduxIntl";

/* Client languages API is needed for the language selector dropdown */
export const FETCH_CLIENT_LANGUAGES_BEGIN = "FETCH_CLIENT_LANGUAGES_BEGIN";
export const FETCH_CLIENT_LANGUAGES_DONE = "FETCH_CLIENT_LANGUAGES_DONE";
export const FETCH_CLIENT_LANGUAGES_FAILED = "FETCH_CLIENT_LANGUAGES_FAILED";

export const FETCH_LOCALE_DATA = "FETCH_LOCALE_DATA";
export const fetchLocaleData = (langCode, langData) => ({
  type: FETCH_LOCALE_DATA,
  payload: {
    id: langCode,
    resource: langData,
  },
});

export const SET_CLIENT_LANGUAGE_BEGIN = "SET_CLIENT_LANGUAGE_BEGIN";
export const SET_CLIENT_LANGUAGE_DONE = "SET_CLIENT_LANGUAGE_DONE";

// TODO: Remove these 3 lines
const KEY_SELECTED_LANG = "selectedLanguage";

export const fetchClientLanguagesBegin = () => ({
  type: FETCH_CLIENT_LANGUAGES_BEGIN,
});
export const fetchClientLanguagesDone = languages => ({
  type: FETCH_CLIENT_LANGUAGES_DONE,
  payload: languages,
});
export const fetchClientLanguagesFailed = () => ({
  type: FETCH_CLIENT_LANGUAGES_FAILED,
});

export const setClientLanguageBegin = () => ({
  type: SET_CLIENT_LANGUAGE_BEGIN,
});
export const setClientLanguageDone = currentLang => ({
  type: SET_CLIENT_LANGUAGE_DONE,
  currentLang,
});

/* BEGIN: Refactored language selection API */

// Returns 2 choices: 1 - recommended based on ranking, 2 - company level
const getLangChoices = metaLanguages => {
  const storage = localStorage || {}; // SSR check
  const matched = metaLanguages.find(lang => lang.isDefault);
  let cachedLang;

  if (storage[KEY_SELECTED_LANG] && storage[KEY_SELECTED_LANG] !== "undefined") {
    cachedLang = JSON.parse(storage[KEY_SELECTED_LANG]);
    cachedLang = cachedLang.code ? cachedLang : "";
  }

  return cachedLang || matched || { id: "enus", code: "en-us", name: "English" };
};

const decideSelection = (cachedLang, newLanguage = "") => {
  let userLang = newLanguage;

  if (userLang.length === 0) {
    userLang = cachedLang.id;
  }

  return userLang;
};

const applySelection = (metaLang, clientName, clientLocaleData) => {

  return dispatch => {
    let localeData = clientLocaleData[metaLang.id];
    let depLocaleData;
    if (localeData) {
      depLocaleData = Promise.resolve(false);
    } else {
      depLocaleData = api.getClientLocaleData(clientName, metaLang.id);
    }

    return depLocaleData.then(resp => {
      if (resp) {
        localeData = resp.body;
        dispatch(fetchLocaleData(metaLang.id, localeData));
      }

      localStorage.setItem(KEY_SELECTED_LANG, JSON.stringify(metaLang));

      dispatch(updateIntl(metaLang.id.substr(0, 2), metaLang.id, metaLang.code, localeData));
      // Wait for most, if not all, child components to repaint themselves.
      setTimeout(() => dispatch(setClientLanguageDone(metaLang)), 100);
    });
  };
};

export const setLanguageById = langid => {
  return (dispatch, getState) => {
    const { app, localization } = getState();
    const { clientName } = app;
    const { languages, i18nResources } = localization;
    const { all = [] } = languages;
    const nextLangMeta = all.find(lang => lang.id === langid);

    if (isExist(nextLangMeta)) {
      dispatch(applySelection(nextLangMeta, clientName, i18nResources));
    }
  };
};

export const setLanguageByCode = (langid = "") => {
  
  return (dispatch, getState) => {
    const { app, localization } = getState();
    const { clientName } = app;
    const { i18nResources } = localization;

    if (isExist(langid)) {
      langid = langid.replace("-", "").toLowerCase();
    }

    dispatch(fetchClientLanguagesBegin());

    return api.getClientLanguages(clientName).then(response => {
      const langList = response.body;

      dispatch(fetchClientLanguagesDone(langList));

      const defaultLang = getLangChoices(langList);
      let metaLang;
     
      // NOTE:
      // - case 1 : open page or refresh (use localstorage one or default one - English)
      // - case 2 : access CXAOne with 'langid' parameter (especially from shop)
      if (isExist(langid)) {
        metaLang = langList.find(lang => {
          const id = lang.id.replace("-", "").toLowerCase();
          const code = lang.code.replace("-", "").toLowerCase();

          return [id, code].includes(langid);
        });
      }

      return dispatch(applySelection(metaLang || defaultLang, clientName, i18nResources));
    });
  };
};

/*
Sets the language.  Normally, there should be only 2 triggers:
1) When the app loads (or a browser refresh)...
  e.g. setLanguage('clientcode', null, {...});

2) In the language selector component...
  e.g. setLanguage('clientcode', 'en-us', {...});

@param {String} clientName - Required
@param {String} newLanguage - When the app loads / reloads, this must be null to pick up cached selection.
@param {Object} cachedLocalizationData - This must be the <localization> store/reducer.
*/
export const setLanguage = (
  clientName,
  newLanguage,
  cachedLocalizationData,
  isAuthenticated
) => dispatch => {
  // Begin: Parameters and data dependencies sanity check
  if ((clientName || "").length === 0) {
    return;
  }

  let clientLanguages = cachedLocalizationData.languages || { all: [] };
  let clientLocaleData = cachedLocalizationData.i18nResources;
  let newLang = (newLanguage || "").replace("-", "");
  let metaLanguages = clientLanguages.all;

  if (newLang && newLang === cachedLocalizationData.selectedLanguage.id) {
    return;
  }

  dispatch(setClientLanguageBegin());

  let depLanguages;
  if (metaLanguages.length > 0) {
    depLanguages = Promise.resolve(false);
  } else {
    dispatch(fetchClientLanguagesBegin());
    depLanguages = api.getClientLanguages(clientName);
  }

  // Need to make sure client language options are already accessible
  depLanguages
    .then(resp => {
      if (resp) {
        metaLanguages = resp.body;
        dispatch(fetchClientLanguagesDone(metaLanguages));
      }

      return Promise.resolve(true);
    })
    .then(resp => {
      const defaultLang = getLangChoices(metaLanguages);
      const finalLangCode = decideSelection(defaultLang, newLang, isAuthenticated);
      const metaLang = metaLanguages.find(lang => lang.id === finalLangCode);

      dispatch(applySelection(metaLang, clientName, clientLocaleData));
    })
    .catch(reason => {
      setTimeout(() => dispatch(setClientLanguageDone(newLang)), 100);
    });
};

/* END: Refactored language selection API */

export const UPDATE = "@@intl/UPDATE";
export function updateIntlAction({ locale, messages, formats }) {
  return {
    type: UPDATE,
    payload: {
      locale,
      messages,
      ...(formats ? { formats } : {}),
    },
  };
}



// WEBPACK FOOTER //
// ./actions/localizationActions.jsx